/**
 * Provisioning → Projects tab.
 *
 * Per-project feature configuration for administrators.
 * Pick a project, then manage its feature assignments. ProjectSelector pairs
 * the server and project pickers in one compound control, so this tab adds no
 * server picker of its own.
 */

import { useState, type ReactElement } from 'react';
import { ProjectSelector } from '@features/workspace/components/ProjectSelector';
import { ProjectFeatureManager } from '@features/devcontainer/components/admin/ProjectFeatureManager';
import { AuditLogTable } from '@features/devcontainer/components/admin/AuditLogTable';
import {
  useProjectPack,
  useDetachProjectFromPack,
} from '@features/devcontainer/hooks/useAdminPacks';
import type { GitProject } from '@features/git/types/git.types';

function ProjectConfigSection({
  project,
}: {
  project: GitProject;
}): ReactElement {
  const { data: packLink } = useProjectPack(project.id);
  const detach = useDetachProjectFromPack();

  const handleDetach = (): void => {
    detach.mutate(project.id);
  };

  return (
    <div className="project-config">
      <h2 className="project-config__title">
        {project.path}
      </h2>
      <ProjectFeatureManager
        projectId={project.id}
        projectName={project.path}
        packLink={packLink}
        onDetachPack={handleDetach}
      />
      <AuditLogTable projectId={project.id} />
    </div>
  );
}

export function ProjectsTab(): ReactElement {
  const [selected, setSelected] = useState<GitProject | null>(null);

  return (
    <>
      <p className="admin-page__subtitle">
        Select a project to manage its devcontainer feature assignments.
      </p>

      <ProjectSelector selectedProject={selected} onSelect={setSelected} />

      {selected && (
        <ProjectConfigSection project={selected} />
      )}
    </>
  );
}
